import { 
  BrainIcon, 
  SparklesIcon, 
  MessageSquareIcon,
  ImageIcon,
  ShieldIcon,
  ArrowRightIcon
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/form/Button';

const features = [
  {
    icon: <BrainIcon className="h-8 w-8 text-primary-600" />,
    title: "AI Mentor",
    description: "Get personalized feedback and different perspectives on your questions from rational, ethical, emotional, and economic viewpoints.",
    benefits: [
      'Multiple perspective analysis',
      'Personalized feedback',
      'Decision guidance',
      'Alternative viewpoints'
    ]
  },
  {
    icon: <SparklesIcon className="h-8 w-8 text-purple-600" />,
    title: "AI Enhance",
    description: "Improve your questions with AI-powered spelling and grammar enhancements for clearer, more engaging SNAPs.",
    benefits: [
      'Grammar correction',
      'Style improvements',
      'Clarity enhancement',
      'Better engagement'
    ]
  },
  {
    icon: <MessageSquareIcon className="h-8 w-8 text-blue-600" />,
    title: "AI Option Generator",
    description: "Let AI suggest additional answer options to provide more comprehensive voting choices.",
    benefits: [
      'Alternative suggestions',
      'Balanced options',
      'Creative additions',
      'Better coverage'
    ]
  },
  {
    icon: <ImageIcon className="h-8 w-8 text-green-600" />,
    title: "AI Image Guard",
    description: "Automatic review and validation of images to ensure appropriate and safe content.",
    benefits: [
      'Content validation',
      'Safety checks',
      'Inappropriate content detection',
      'Real-time scanning'
    ],
    comingSoon: true
  },
  {
    icon: <ShieldIcon className="h-8 w-8 text-red-600" />,
    title: "AI Speech Guard",
    description: "Advanced protection against hate speech and insults in comments and discussions.",
    benefits: [
      'Hate speech prevention',
      'Insult detection',
      'Safe discussions',
      'Real-time moderation'
    ],
    comingSoon: true
  },
  {
    icon: <ShieldIcon className="h-8 w-8 text-green-600" />,
    title: "Privacy Protection",
    description: "Industry-leading privacy measures ensure your data stays safe while using our AI features.",
    benefits: [
      'No data collection',
      'Anonymous processing',
      'No behavior tracking',
      'GDPR compliant'
    ],
    link: {
      to: "privacy-protection",
      text: "Learn more about our privacy measures"
    }
  }
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function AIFeatures() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate('/create');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="lg:text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">AI Features</h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Intelligent Decision Support
          </p>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
            Harness the power of AI to make better decisions while maintaining complete privacy and control.
            <span className="ml-2 text-sm text-primary-600">(Coming April 1st, 2025)</span>
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mt-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                variants={item}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    {feature.icon}
                    {feature.comingSoon && (
                      <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded-full">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 mb-6">{feature.description}</p>
                  <ul className="space-y-3">
                    {feature.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-center text-sm text-gray-600">
                        <span className="mr-2 text-primary-600">✓</span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                  {feature.link && (
                    <ScrollLink
                      to={feature.link.to}
                      smooth={true}
                      duration={500}
                      offset={-50}
                      className="mt-6 inline-flex items-center text-green-600 hover:text-green-700 cursor-pointer"
                    >
                      {feature.link.text}
                      <ArrowRightIcon className="ml-2 h-4 w-4" />
                    </ScrollLink>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Button
            onClick={handleGetStarted}
            variant="primary"
            className="px-8 py-3 md:py-4 md:px-10 text-base md:text-lg"
          >
            Create SNAP
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
}